import type { Verdict } from "./answer.js";
import { sitsForExam, usedHelp, type CardForm, type TermBoxes } from "./leitner.js";
import { MATCH_PAIRS } from "./schemas.js";

/**
 * What the end of a round reports (docs/UX.md §4, issue #47). It counts
 * words, not answers: a word asked five times is still one word.
 */
export interface RoundSummary {
  /** Distinct words in the round. */
  words: number;
  /** Words where every direction asked was right on its first attempt, without help. */
  firstTry: number;
  /** Words that sit after this round and did not before (both directions). */
  newlySitting: number;
}

export interface RoundAnswer {
  term_id: string;
  direction: "ab" | "ba";
  verdict: Verdict;
  /** Defaults to typing, like `practiceResult`. */
  form?: CardForm;
}

export function summarizeRound(
  answers: readonly RoundAnswer[],
  before: ReadonlyMap<string, TermBoxes>,
  after: ReadonlyMap<string, TermBoxes>,
): RoundSummary {
  const unasked: TermBoxes = { ab: 0, ba: 0 };
  const firstVerdict = new Map<string, Verdict>();
  const missedFirst = new Set<string>();
  for (const answer of answers) {
    const key = `${answer.term_id}:${answer.direction}`;
    if (firstVerdict.has(key)) continue;
    firstVerdict.set(key, answer.verdict);
    // A first answer with tiles shown on request is not «auf Anhieb» (issue #63).
    const boxBefore = (before.get(answer.term_id) ?? unasked)[answer.direction];
    if (answer.verdict === "wrong" || usedHelp(answer.form ?? "type", boxBefore)) {
      missedFirst.add(answer.term_id);
    }
  }

  const words = [...new Set(answers.map((a) => a.term_id))];
  let newlySitting = 0;
  for (const word of words) {
    const was = sitsForExam(before.get(word) ?? unasked);
    const is = sitsForExam(after.get(word) ?? unasked);
    if (is && !was) newlySitting += 1;
  }

  return {
    words: words.length,
    firstTry: words.filter((word) => !missedFirst.has(word)).length,
    newlySitting,
  };
}

/** What a card needs to go on the warm-up board. */
export interface BoardCard {
  term_id: string;
  direction: "ab" | "ba";
  mode: CardForm;
}

/**
 * The warm-up board (issue #63): five new words, moved to the front of the
 * round. Only cards the box asks with tiles anyway may go on it -- a word
 * past its first encounter is typed, never matched. Never the same word
 * twice: with both directions of one word on the board, the answer to one row
 * is the question of another. One direction is preferred so the columns stay
 * one language each; mixed is better than no board at all. Null when the
 * round has fewer than five new words.
 */
export function stageWarmUp<T extends BoardCard>(cards: readonly T[]): T[] | null {
  const pick = (direction: "ab" | "ba" | null) => {
    const seen = new Set<string>();
    const chosen: T[] = [];
    for (const card of cards) {
      if (chosen.length === MATCH_PAIRS) break;
      if (card.mode !== "choice" || seen.has(card.term_id)) continue;
      if (direction && card.direction !== direction) continue;
      seen.add(card.term_id);
      chosen.push(card);
    }
    return chosen;
  };

  const first = cards.find((card) => card.mode === "choice");
  const sameDirection = pick(first?.direction ?? null);
  const chosen = sameDirection.length === MATCH_PAIRS ? sameDirection : pick(null);
  if (chosen.length < MATCH_PAIRS) return null;
  return [...chosen, ...cards.filter((card) => !chosen.includes(card))];
}
